/* eslint-disable no-nested-ternary */
import { toast } from 'react-toastify';
import { useEffect, useRef, useState } from 'react';
import { getProducts } from '../../../../services/dashboard.service';
import { debounce, renderRightIconForSearchInput } from '../../../../utils/global';
import InputComponent from '../../../common/InputComponent/InputComponent';
import SearchIcon from '../../../../assets/icons/SearchIcon';
import SaralLoaderImage from '../../../common/SaralLoader/SaralLoaderImage';
import SearchFTUX from '../../../common/SearchFTUX/SearchFTUX';
import ProductSearchDropdownItem from './ProductSearchDropdownItem';
import styles from './productSearch.module.css';
import useIsMobile from '../../../../hooks/useIsMobile';

interface Props {
  products: any[];
  setProducts: React.Dispatch<React.SetStateAction<any>>;
  previewProducts: any[];
  setPreviewProducts: React.Dispatch<React.SetStateAction<any>>;
  searchStates: { isLoading: boolean; searchQuery: string | undefined };
  handleUpdateSearchStates: <K extends keyof { isLoading: boolean; searchQuery: string | undefined }>(
    key: K,
    value: { isLoading: boolean; searchQuery: string | undefined }[K]
  ) => void;
  label?: string;
  maxLimit: number | null;
  disableInput?: boolean;
  popOverHeight?: string;
  defaultCurrency: string;
}

export default function ProductSection({
  products,
  setProducts,
  previewProducts,
  setPreviewProducts,
  searchStates,
  handleUpdateSearchStates,
  label = 'Select Products to Automate',
  maxLimit,
  disableInput = false,
  popOverHeight = '296px',
  defaultCurrency,
}: Props) {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);
  const [isInputFocused, setInputFocused] = useState(false);
  const [isResultsNull, setIsResultsNull] = useState(false);

  const isMobile = useIsMobile();

  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const resetProducts = () => {
    setProducts([]);
    setPreviewProducts([]);
  };

  /**
   * function to search products
   */
  const searchProducts = async (query: string) => {
    setIsResultsNull(false);
    handleUpdateSearchStates('isLoading', true);
    const response = await getProducts(query);
    if (response?.status) {
      setProducts(response.data);
      if (response.data.length === 0) {
        setIsResultsNull(true);
      }
    } else {
      toast.error(response?.message || 'Something went wrong.');
    }
    handleUpdateSearchStates('isLoading', false);
  };

  /**
   * function handle debouncing while calling search API
   */
  const handleSearch = debounce((value: string) => {
    if (value.length > 0) {
      searchProducts(value);
    } else {
      resetProducts();
    }
  }, 1000);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        inputWrapperRef.current &&
        !inputWrapperRef.current.contains(event.target as Node)
      ) {
        setIsPopoverOpen(false);
      }
    };

    if (isPopoverOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPopoverOpen]);

  const isMaxLimitReached = Boolean(maxLimit) && Boolean(previewProducts.length === maxLimit);
  return (
    <div>
      <div ref={inputWrapperRef}>
        <InputComponent
          placeholder={isMaxLimitReached ? 'Max Limit Reached' : 'Search for a product'}
          label={
            <span className="flex flex-start gap-1">
              {label} <span className="text-critical">*</span>
            </span>
          }
          disabled={isMaxLimitReached || disableInput}
          labelRight={maxLimit && <span className="text-disabled">(Max {maxLimit})</span>}
          labelStyle={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
          left={<SearchIcon />}
          value={searchStates.searchQuery}
          inputClassName={`${isMobile ? 'text-sm' : 'text-base'}`}
          setValue={(value: string) => {
            handleUpdateSearchStates('searchQuery', value);
            handleSearch(value);
            setIsPopoverOpen(true);
          }}
          onFocus={() => {
            setInputFocused(true);
          }}
          onBlur={() => {
            setInputFocused(false);
          }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            if (!anchorEl) {
              setAnchorEl(e.currentTarget);
            }
          }}
          right={renderRightIconForSearchInput({
            loading: searchStates.isLoading,
            query: searchStates.searchQuery || '',
            setQuery: (value: string) => {
              handleUpdateSearchStates('searchQuery', value);
              resetProducts();
              setIsPopoverOpen(false);
            },
          })}
          inputWrapperClass={isMaxLimitReached || disableInput ? 'bg-disabled' : ''}
        />
      </div>

      {isPopoverOpen && (
        <div
          style={{ width: inputWrapperRef?.current?.clientWidth }}
          className={styles.inputElementPopover}
          ref={popoverRef}
        >
          {searchStates?.searchQuery && searchStates?.searchQuery?.length > 0 && (
            <>
              <div className={styles.popoverProductsList} style={{ height: popOverHeight }} ref={scrollRef}>
                {(isInputFocused && products.length === 0 && !isResultsNull) || searchStates.isLoading ? (
                  <div className="flex justify-center items-center h-full flex-col">
                    <SaralLoaderImage />
                    <span className="text-sm text-subdued">Searching for Products....</span>
                  </div>
                ) : products.length === 0 ? (
                  <SearchFTUX text="No Results Found!" className="h-full" />
                ) : (
                  products.map((product, i) => {
                    const isChecked = previewProducts.some((p) => p?.id === product.id);
                    return (
                      <ProductSearchDropdownItem
                        key={i}
                        product={product}
                        isChecked={isChecked}
                        previewProducts={previewProducts}
                        setPreviewProducts={setPreviewProducts}
                        index={i}
                        maximumAllowedSelection={maxLimit}
                        defaultCurrency={defaultCurrency}
                      />
                    );
                  })
                )}
              </div>
              {products.length > 0 && !searchStates.isLoading && (
                <div className={styles.dropdownButtonContainer}>
                  {isMaxLimitReached && (
                    <p className="text-center text-subdued text-xs mb-2">You can select {maxLimit} products max.</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
